/**
 * Notifications Hook
 * Custom hook for managing notifications with real-time updates and tab filtering
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import type { Notification, NotificationTab } from "../services/notification.service"
import * as notificationService from "../services/notification.service"

/**
 * Resolve the target route for a notification. Chat-type notifications route
 * into the conversation view; everything else falls back to the sender's
 * profile so the existing behavior is preserved.
 */
function getNotificationRoute(notification: Notification): string | null {
  if (notification.entityId) {
    switch (notification.entityType) {
      case "conversation":
        return `/chats/dm/${notification.entityId}`
      case "ritual_group_conversation":
        return `/chats/ritual/${notification.entityId}`
      case "ai_conversation":
        return `/chats/ai/${notification.entityId}`
    }
  }

  const isCommentAction =
    notification.action === "comment_posted" ||
    notification.action === "comment_reply" ||
    notification.action === "comment_mention"

  if (notification.entityType === "ritual") {
    const ritualId =
      (notification.data as Record<string, unknown>)?.ritualId ?? notification.entityId
    if (ritualId) return `/rituals/${ritualId}${isCommentAction ? "#discussion" : ""}`
  }

  if (notification.entityType === "community") {
    const communityId =
      (notification.data as Record<string, unknown>)?.communityId ?? notification.entityId
    if (communityId) return `/communities/${communityId}${isCommentAction ? "?tab=discussion" : ""}`
  }

  if (notification.action === "session_booked") {
    return "/my-rituality?tab=sessions&subTab=coaching"
  }

  if (notification.triggeredByUser) {
    return `/profile/${notification.triggeredByUser.id}`
  }

  return null
}

interface UseNotificationsOptions {
  enabled?: boolean
  tab?: NotificationTab
  /** Callback called after navigating from a notification click (e.g., to close sheet) */
  onAfterNavigate?: () => void
}

export function useNotifications(_options: UseNotificationsOptions = {}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const tab = _options.tab ?? "all"
  const { onAfterNavigate } = _options

  // Get accurate unread count from server (filtered by tab)
  const { data: serverUnreadCount } = useUnreadCount(tab)

  // Infinite query for notifications (filtered by tab)
  const query = useInfiniteQuery({
    queryKey: ["notifications", tab],
    queryFn: ({ pageParam = 1 }) => {
      return notificationService.getNotifications(pageParam as number, 20, tab)
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      // API returns { notifications, total, page, pages }
      // Return next page number if exists
      if (lastPage && lastPage.page < lastPage.pages) {
        return lastPage.page + 1
      }
      return undefined
    },
    // Keep data fresh for 5 minutes - socket events update in real-time
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // Only refetch on window focus
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Always enabled to properly manage cache - socket events keep it fresh
    enabled: true,
  })

  // Flatten all pages into a single array for easier consumption
  const notifications =
    query.data?.pages.flatMap(page => {
      return page.notifications || []
    }) || []

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onMutate: id => {
      // Optimistically update across all pages
      queryClient.setQueryData(["notifications", tab], (old: unknown) => {
        const currentData = old as { pages: Array<{ notifications: Notification[] }> } | undefined
        if (!currentData) return old

        return {
          ...currentData,
          pages: currentData.pages.map(page => ({
            ...page,
            notifications: page.notifications.map(n =>
              n.id === id ? { ...n, isRead: true, readAt: new Date() } : n,
            ),
          })),
        }
      })

      // Optimistically decrement unread count
      queryClient.setQueryData(["unread-count", tab], (old: unknown) => {
        const currentCount = old as number | undefined
        return Math.max(0, (currentCount || 0) - 1)
      })
    },
    onSuccess: () => {
      // Invalidate to ensure server state is reflected
      queryClient.invalidateQueries({ queryKey: ["notifications", tab] })
      queryClient.invalidateQueries({ queryKey: ["unread-count", tab] })
    },
  })

  const markAsUnreadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markAsUnread(id),
    onMutate: id => {
      // Optimistically update across all pages
      queryClient.setQueryData(["notifications", tab], (old: unknown) => {
        const currentData = old as { pages: Array<{ notifications: Notification[] }> } | undefined
        if (!currentData) return old

        return {
          ...currentData,
          pages: currentData.pages.map(page => ({
            ...page,
            notifications: page.notifications.map(n =>
              n.id === id ? { ...n, isRead: false, readAt: null } : n,
            ),
          })),
        }
      })

      // Optimistically increment unread count
      queryClient.setQueryData(["unread-count", tab], (old: unknown) => {
        const currentCount = old as number | undefined
        return (currentCount || 0) + 1
      })
    },
    onSuccess: () => {
      // Invalidate to ensure server state is reflected
      queryClient.invalidateQueries({ queryKey: ["notifications", tab] })
      queryClient.invalidateQueries({ queryKey: ["unread-count", tab] })
    },
  })

  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onMutate: () => {
      // Optimistically update all notifications across all pages
      queryClient.setQueryData(["notifications", tab], (old: unknown) => {
        const currentData = old as { pages: Array<{ notifications: Notification[] }> } | undefined
        if (!currentData) return old

        return {
          ...currentData,
          pages: currentData.pages.map(page => ({
            ...page,
            notifications: page.notifications.map(n => ({
              ...n,
              isRead: true,
              readAt: new Date(),
            })),
          })),
        }
      })

      // Optimistically set unread count to 0
      queryClient.setQueryData(["unread-count", tab], 0)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", tab] })
      queryClient.invalidateQueries({ queryKey: ["unread-count", tab] })
    },
  })

  const deleteBroadcastMutation = useMutation({
    mutationFn: (notificationId: string) =>
      notificationService.deleteBroadcastNotification(notificationId),
    onMutate: notificationId => {
      queryClient.setQueryData(["notifications", tab], (old: unknown) => {
        const currentData = old as
          | {
              pages: Array<{ notifications: Notification[]; total?: number }>
            }
          | undefined
        if (!currentData) return old

        return {
          ...currentData,
          pages: currentData.pages.map((page, index) => ({
            ...page,
            notifications: page.notifications.filter(n => n.id !== notificationId),
            total: index === 0 ? Math.max(0, (page.total || 0) - 1) : page.total,
          })),
        }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", tab] })
      queryClient.invalidateQueries({ queryKey: ["unread-count", tab] })
    },
  })

  const handleNotificationClick = (notification: Notification): void => {
    // Mark as read
    markAsReadMutation.mutate(notification.id)

    const route = getNotificationRoute(notification)
    if (route) {
      navigate(route)
      // Close sheet after navigation
      onAfterNavigate?.()
    }
  }

  return {
    notifications,
    unreadCount: serverUnreadCount ?? 0,
    isLoading: query.isLoading,
    error: query.error,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    markAsRead: markAsReadMutation.mutate,
    markAsUnread: markAsUnreadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    deleteBroadcast: deleteBroadcastMutation.mutate,
    handleNotificationClick,
  }
}

export function useUnreadCount(tab: NotificationTab = "all") {
  return useQuery({
    queryKey: ["unread-count", tab],
    queryFn: () => notificationService.getUnreadCount(tab),
    // Socket events keep cache fresh - only refetch on window focus
    refetchOnWindowFocus: true,
  })
}
